import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";
import { formatPhone } from "@/lib/utils";
import deckImg from "@/assets/photos-real/deck.jpg";

/**
 * WarmParallax — a full-bleed statement band whose real-photo background drifts
 * slower than the page as you scroll, creating a subtle depth illusion (the same
 * transform-based parallax used on the braiding/auto sites — not the janky
 * `background-attachment: fixed`, which iOS Safari disables).
 *
 * Honest imagery: uses a real still of the Lynnwood home, so it's only rendered
 * for that location (Seattle has no footage of its own).
 * Reduced-motion: the listener is never attached, so the photo sits still.
 */
export function WarmParallax() {
  const { location, isSeattle } = useLocation();
  const basePath = isSeattle ? "/seattle" : "";
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.bottom < 0 || r.top > vh) return; // skip when off-screen
        // Distance of the band's center from the viewport center; the bg moves
        // opposite at a fraction of that, clamped so the scaled image never gaps.
        const raw = (r.top + r.height / 2 - vh / 2) * -0.3;
        setOffset(Math.max(-150, Math.min(150, raw)));
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={ref}
      aria-label="Come see our home in person"
      className="relative flex min-h-[58vh] items-center justify-center overflow-hidden md:min-h-[66vh]"
    >
      {/* Parallax background — real still, scaled so the drift never reveals an edge. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage: `url(${deckImg})`,
          transform: `translate3d(0, ${offset}px, 0) scale(1.85)`,
        }}
      />
      {/* Legibility scrim — brand navy, top-to-bottom. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, hsl(213 57% 10% / 0.5) 0%, hsl(213 57% 10% / 0.72) 100%)",
        }}
      />

      <div className="reveal container relative z-10 mx-auto px-6 text-center">
        <span className="eyebrow text-[hsl(var(--accent-on-dark))]">Come See for Yourself</span>
        <h2 className="mx-auto mt-4 max-w-3xl display-lg font-heading font-semibold tracking-[-0.03em] text-white">
          The best way to know a home is to{" "}
          <span className="font-display-italic font-normal text-[hsl(var(--accent-on-dark))]">be in it.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-white/85">
          Photos and video only say so much. Come walk through, meet the caregivers,
          and see exactly where your parent would live.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
          <a
            href={`tel:${location.primaryPhone.replace(/-/g, "")}`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-semibold text-accent-foreground shadow-lg transition-transform active:scale-[0.98]"
          >
            <Phone className="h-4 w-4" />
            Call {formatPhone(location.primaryPhone)}
          </a>
          <Link
            to={`${basePath}/contact#tour-form`}
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/35 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/[0.12]"
          >
            Schedule a private tour
            <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
